import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CUP_TYPES = ["Double Wall Cups", "Cutting Chai Cups", "Single Wall Cold Cups", "Ripple Wall Chai Cups"];
const SIZES = ["250 ml", "350 ml", "500 ml", "600 ml"];
const QUANTITIES = ["5,000", "10,000", "25,000", "50,000", "1 Lakh+"];
const DESIGN_OPTS = ["I have artwork", "Design it for me (free)"];

const Chip = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-sm font-bold rounded-full px-4 py-2 transition-all duration-300 border ${
      selected
        ? "bg-[#FF5A36] text-white border-[#FF5A36] shadow-lg shadow-[#FF5A36]/25"
        : "bg-white text-[#2E1065]/70 border-[#2E1065]/15 hover:border-[#FF5A36]/50"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const inputCls =
  "w-full bg-white border border-[#2E1065]/15 focus:border-[#FF5A36] focus:ring-2 focus:ring-[#FF5A36]/20 rounded-xl px-4 py-3 text-sm font-medium text-[#2E1065] placeholder:text-[#2E1065]/35 outline-none transition-all duration-300";

export const QuoteForm = ({ presetType, compact = false, idPrefix = "quote", onSuccess }) => {
  const [form, setForm] = useState({
    brand_name: "",
    contact_name: "",
    email: "",
    cup_type: presetType || CUP_TYPES[0],
    cup_size: SIZES[1],
    quantity: QUANTITIES[1],
    design_option: DESIGN_OPTS[1],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (presetType) setForm((f) => ({ ...f, cup_type: presetType }));
  }, [presetType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_name || !form.contact_name || !form.email) {
      toast.error("Please fill your brand, name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received!", {
        description: `${form.cup_type} · ${form.cup_size} · ${form.quantity} pcs — our team replies within 24 hours.`,
      });
      setForm({ ...form, brand_name: "", contact_name: "", email: "", message: "" });
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`${idPrefix}-form`}>
      <div className={`grid ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <input
          className={inputCls}
          placeholder="Brand / Business name *"
          value={form.brand_name}
          onChange={(e) => set("brand_name", e.target.value)}
          data-testid={`${idPrefix}-brand-input`}
        />
        <input
          className={inputCls}
          placeholder="Your name *"
          value={form.contact_name}
          onChange={(e) => set("contact_name", e.target.value)}
          data-testid={`${idPrefix}-contact-input`}
        />
      </div>
      <input
        type="email"
        className={inputCls}
        placeholder="Email address *"
        value={form.email}
        onChange={(e) => set("email", e.target.value)}
        data-testid={`${idPrefix}-email-input`}
      />

      <div>
        <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Cup type</p>
        <div className="flex flex-wrap gap-2">
          {CUP_TYPES.map((t) => (
            <Chip key={t} testid={`${idPrefix}-cup-type-${t.toLowerCase().replace(/ & | /g, "-")}`} selected={form.cup_type === t} onClick={() => set("cup_type", t)}>
              {t}
            </Chip>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Size</p>
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <Chip key={s} testid={`${idPrefix}-size-${s.replace(" ", "-")}`} selected={form.cup_size === s} onClick={() => set("cup_size", s)}>
                {s}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Quantity</p>
          <div className="flex flex-wrap gap-2">
            {QUANTITIES.map((q) => (
              <Chip key={q} testid={`${idPrefix}-quantity-${q.replace(/[,+]/g, "").replace(/ /g, "-").toLowerCase()}`} selected={form.quantity === q} onClick={() => set("quantity", q)}>
                {q}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-black uppercase tracking-widest text-[#2E1065]/50 mb-2.5">Artwork</p>
        <div className="flex flex-wrap gap-2">
          {DESIGN_OPTS.map((d) => (
            <Chip key={d} testid={`${idPrefix}-design-${d.startsWith("I have") ? "have-artwork" : "design-service"}`} selected={form.design_option === d} onClick={() => set("design_option", d)}>
              {d}
            </Chip>
          ))}
        </div>
      </div>

      <textarea
        className={`${inputCls} min-h-[90px] resize-none`}
        placeholder="Anything else? colours, deadlines, vendor locations…"
        value={form.message}
        onChange={(e) => set("message", e.target.value)}
        data-testid={`${idPrefix}-message-input`}
      />

      <p className="text-xs text-[#2E1065]/50 leading-relaxed" data-testid={`${idPrefix}-privacy-note`}>
        We use these details only to prepare your quote and reply to you. Never sold, never shared.{" "}
        <a href="/privacy" className="font-bold text-[#0D9488] underline underline-offset-2">Privacy Policy</a>.
      </p>

      <button
        type="submit"
        disabled={loading}
        className="group w-full flex items-center justify-center gap-2 font-bold text-white bg-[#2E1065] hover:bg-[#FF5A36] rounded-full px-6 py-4 transition-all duration-300 disabled:opacity-60"
        data-testid={`${idPrefix}-submit-button`}
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />}
        {loading ? "Sending…" : "Send Quote Request"}
      </button>
    </form>
  );
};
